import { contrastRatio, parseCssColor } from './segment-contrast.js';
import { storedColorsFollowTheme } from './viewer-theme.js';
/** Below this ratio the viewer's own text is invisible for practical purposes. */
const MIN_VIEWER_CONTRAST = 3;
/**
 * Keep a viewer's stored text/background pair only if it is legible.
 *
 * The transcript viewer persists its colours in localStorage. A pair that
 * cannot be read — white on white after an old dark preset plus a manual
 * background change, or a value an older build wrote — survives every
 * code fix because nothing ever re-validates it. QA saw exactly that on one
 * Windows browser for three months (MP-182: "text is white, the caret
 * moves"). The segment guard cannot help: it measures writer colours
 * against the viewer surface, and here the surface itself is broken.
 *
 * Returns the stored pair when both parse, both are opaque, and contrast is
 * at least 3:1 — otherwise the supplied defaults.
 */
export function legibleViewerColors(stored, defaults, 
/**
 * Called when a repair actually happens. This is the one site that KNOWS a
 * real corruption occurred — `followedViewerColors` only sees the result
 * and is right to stay pure and silent. The repair kept MP-182 invisible
 * for three months precisely because nothing counted it, so callers pass a
 * reporter here to make the bug class measurable without changing anything
 * the reader sees. Optional, and never throws into the caller.
 */
onRepair) {
    const fg = parseCssColor(stored.textColor);
    const bg = parseCssColor(stored.backgroundColor);
    // Opacity first: `parseCssColor` drops alpha, so `rgba(255,255,255,0)` on a
    // dark ground measures as a maximal-contrast pair and would be kept
    // verbatim — text that is invisible for the most literal possible reason,
    // which is exactly the MP-182 symptom this function exists to stop.
    const opaque = isOpaqueCssColor(stored.textColor) && isOpaqueCssColor(stored.backgroundColor);
    if (fg && bg && opaque && contrastRatio(fg, bg) >= MIN_VIEWER_CONTRAST)
        return stored;
    try {
        if (!fg || !bg)
            onRepair?.('unparseable');
        else if (!opaque)
            onRepair?.('translucent');
        else
            onRepair?.('illegible');
    }
    catch {
        // A broken reporter must never cost the reader their colours.
    }
    return { ...stored, textColor: defaults.textColor, backgroundColor: defaults.backgroundColor };
}
/** Where the transcript viewer persists its settings. */
export const TRANSCRIPT_VIEWER_SETTINGS_KEY = 'transcriptViewerSettings';
/**
 * True when a CSS colour is fully opaque.
 *
 * `parseCssColor` deliberately drops alpha — it answers "what hue is this",
 * which is all the contrast maths needs. But a caller that PAINTS the colour
 * has to care: `rgba(0, 0, 0, 0)` measures as opaque black and renders as
 * nothing, so a transparent ground would pass a contrast check and then let
 * whatever is underneath show through at an unknown effective contrast.
 */
export function isOpaqueCssColor(value) {
    const v = value.trim().toLowerCase();
    const hex = v.match(/^#([0-9a-f]+)$/);
    if (hex) {
        const h = hex[1];
        if (h.length === 4)
            return h[3] === 'f';
        if (h.length === 8)
            return h.slice(6) === 'ff';
        return h.length === 3 || h.length === 6;
    }
    // Find the alpha SLOT rather than pattern-matching the last number. A
    // greedy `[^)]*[,/]` read the final channel of a 3-component colour as
    // alpha, so `rgb(0, 0, 0)` and `hsl(210, 50%, 40%)` were both reported
    // see-through — failing safe, but wrong, and this is an exported predicate
    // whose whole contract is "is this opaque".
    const fn = v.match(/^(?:rgba?|hsla?)\(([^)]*)\)$/);
    if (fn) {
        const args = fn[1];
        // Modern syntax puts alpha after a slash, whatever separates the
        // channels: `rgb(0 0 0 / 50%)`, `hsl(210 50% 40% / .5)`.
        const slashIndex = args.indexOf('/');
        let slot = null;
        if (slashIndex !== -1) {
            slot = args.slice(slashIndex + 1).trim();
        }
        else {
            // Legacy syntax separates every component with commas, so a fourth
            // comma-separated component — and only that — is the alpha.
            const parts = args
                .split(',')
                .map((part) => part.trim())
                .filter((part) => part.length > 0);
            if (parts.length === 4)
                slot = parts[3];
            else if (parts.length > 4)
                return false;
        }
        // No alpha component at all means fully opaque.
        if (slot === null)
            return true;
        const raw = Number.parseFloat(slot);
        if (Number.isNaN(raw))
            return false;
        return slot.endsWith('%') ? raw >= 100 : raw >= 1;
    }
    // Any other functional notation — `color()`, `lab()`, `oklch()`, or an
    // `hsl()`/`rgb()` spelling this regex does not model — may carry an alpha
    // channel we cannot see, so refuse it rather than guessing it is opaque.
    // `parseCssColor` understands more syntaxes than this function does, and a
    // value that clears that gate but not this one must not be painted.
    if (/^[a-z-]+\(/.test(v))
        return false;
    // Named colours and plain hex carry no alpha. `transparent` is in the
    // named table and must not slip through.
    return v !== 'transparent';
}
/**
 * The colour pair the transcript viewer is currently painting, or `null` when
 * it has no usable stored choice.
 *
 * The image viewer used to paint a fixed surface, chosen in August to match
 * the transcript viewer's default white. But that colour is a setting, not a
 * constant: a low-vision student who moves the transcript to a dark ground
 * then opens an image and gets a white one. MU asked for the two to agree
 * ("dorovnat barvu prohlizece obrazu s prohlizecem prepisu", 18. 9. 2026).
 *
 * Returns the PAIR, not just the background, because that is what the
 * transcript viewer paints: the ground plus the text and icons on it.
 * Following the ground alone would hand the image viewer a dark page with a
 * light-theme foreground still on it — the opposite of an accessibility fix.
 *
 * `null` means "no opinion — keep your own surface", which leaves the
 * theme-aware default in place rather than inventing colours. Nothing stored,
 * unparseable JSON, a value that is not a colour, a translucent ground, or a
 * pair the transcript viewer itself would reject all return `null`.
 *
 * Takes the raw stored string so it stays pure and testable; the caller
 * supplies `localStorage.getItem(TRANSCRIPT_VIEWER_SETTINGS_KEY)`.
 */
export function followedViewerColors(raw) {
    if (!raw)
        return null;
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (typeof parsed !== 'object' || parsed === null)
        return null;
    const { backgroundColor, textColor } = parsed;
    if (typeof backgroundColor !== 'string' || typeof textColor !== 'string')
        return null;
    // A reader who never picked colours follows the app theme, so the stored
    // pair is not what the transcript paints; keep the image viewer's own
    // theme-aware surface (the dark one matches the transcript's dark pair).
    if (storedColorsFollowTheme(parsed))
        return null;
    // A translucent ground would be painted over the theme surface at an
    // unknown effective contrast, and translucent TEXT measures its contrast
    // as if it were solid — so neither is safe to follow.
    if (!isOpaqueCssColor(backgroundColor) || !isOpaqueCssColor(textColor))
        return null;
    const bg = parseCssColor(backgroundColor);
    const fg = parseCssColor(textColor);
    if (!bg || !fg)
        return null;
    // The transcript viewer replaces an illegible pair wholesale, so following
    // one would paint colours it is not actually using.
    if (contrastRatio(fg, bg) < MIN_VIEWER_CONTRAST)
        return null;
    return { backgroundColor, textColor };
}
//# sourceMappingURL=viewer-colors.js.map